import leaveHome from '@Application/Indicators/leaveHome';
import { PermissionEnum } from '@Application/Security/Roles';
import { css } from '@emotion/react';
import BeneficiariesBlock from '@UI/Pages/Home/BeneficiariesBlock';
import DocumentsBlock from '@UI/Pages/Home/DocumentsBlock';
import HomeHeader from '@UI/Pages/Home/HomeHeader';
import SubjectTypesBlock from '@UI/Pages/Home/SubjectTypesBlock';
import useSubjectTypes from '@UI/Hooks/useSubjectTypes';
import ShortcutCards from '@UI/Pages/Home/ShortcutCards';
import CostsBlock from '@UI/Pages/Home/CostsBlock';
import useCanReadBeneficiaries from '@UI/Pages/Home/Hooks/useCanReadBeneficiaries';
import {
	setHomeDocumentType,
	DEFAULT_HOME_DOCUMENT_TYPE,
} from '@UI/Pages/Home/Signals/homeDocumentTypeSignal';
import { DEFAULT_HOME_PERIOD, setHomePeriod } from '@UI/Pages/Home/Signals/homePeriodSignal';
import { DEFAULT_HOME_SCOPE, setHomeScope } from '@UI/Pages/Home/Signals/homeScopeSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Message, useOnUnmount } from '@verclam/ui-kit';
import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const pageStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.375rem;
`;

const twoColumnsStyle = css`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 1.125rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

const oneColumnStyle = css`
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 1.125rem;
`;

/** Layout of the home screen: the blocks the caller's permissions open, and the shortcuts. */
const HomeContent: React.FC = () => {
	const canReadBeneficiaries = useCanReadBeneficiaries();
	const canReadAiUsage = useHasPermission(PermissionEnum.AI_USAGE_READ);
	const { types } = useSubjectTypes();
	const dispatch = useDispatch();

	// A visit always starts on the defaults and reads its figures again: filters and figures are reset when the page unmounts.
	useOnUnmount(() => {
		setHomeScope(DEFAULT_HOME_SCOPE);
		setHomePeriod(DEFAULT_HOME_PERIOD);
		setHomeDocumentType(DEFAULT_HOME_DOCUMENT_TYPE);
		dispatch(leaveHome());
	});

	const hasSubjectTypes = types !== null && !types.isEmpty;
	const hasNoBlock = !canReadBeneficiaries && !canReadAiUsage && !hasSubjectTypes;

	return (
		<div css={pageStyle}>
			<HomeHeader />
			<ConditionalLoader data={hasNoBlock}>
				<Message severity="info" text="Aucun indicateur n'est disponible pour votre rôle." />
			</ConditionalLoader>

			<ConditionalLoader data={canReadBeneficiaries}>
				<ShortcutCards />
			</ConditionalLoader>

			<SubjectTypesBlock />

			<ConditionalLoader data={canReadBeneficiaries}>
				<DocumentsBlock />
			</ConditionalLoader>

			<ConditionalLoader data={canReadBeneficiaries || canReadAiUsage}>
				<div css={canReadBeneficiaries && canReadAiUsage ? twoColumnsStyle : oneColumnStyle}>
					<ConditionalLoader data={canReadBeneficiaries}>
						<BeneficiariesBlock />
					</ConditionalLoader>

					<ConditionalLoader data={canReadAiUsage}>
						<CostsBlock />
					</ConditionalLoader>
				</div>
			</ConditionalLoader>
		</div>
	);
};

export default HomeContent;
